/* oxlint-disable jsx-a11y/no-noninteractive-element-to-interactive-role -- ARIA 1.2 combobox: <input role="combobox"> + <li role="option"> is the spec'd pattern */
import { useId, useMemo, useState } from 'react';
import { MapPin } from 'lucide-react';
import { cx } from '../../lib/util/cx';
import type { LocaleActivityRow } from './api';

const MAX_RESULTS = 8;

/** Rows whose name, city or state contains every word of the term. */
export function findLocales(rows: LocaleActivityRow[], term: string): LocaleActivityRow[] {
  const words = term.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  return rows
    .filter((r) => {
      const hay = `${r.name} ${r.city} ${r.stateProvince}`.toLowerCase();
      return words.every((w) => hay.includes(w));
    })
    .slice(0, MAX_RESULTS);
}

/**
 * Jump-to-locale for the activity map. NOT a filter: it searches the locales
 * already on the map, and picking one (click or Enter) does what clicking its
 * shape does — zoom to it and open its card — then clears itself for the next
 * search. Styled like the LocalePicker.
 */
export function LocaleFinder({ rows, onPick }: { rows: LocaleActivityRow[]; onPick: (localeId: string) => void }) {
  const id = useId();
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const options = useMemo(() => findLocales(rows, term), [rows, term]);
  const showList = open && term.trim().length > 0;

  const pick = (row: LocaleActivityRow) => {
    onPick(row.localeId);
    setTerm('');
    setActive(0);
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((a) => Math.min(a + 1, Math.max(options.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter' && showList && options[active]) {
      e.preventDefault();
      pick(options[active]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div className="relative w-full max-w-[320px] font-body">
      <div className="flex min-h-[38px] items-center gap-1.5 rounded-input border border-[rgba(143,68,32,.12)] bg-white px-2.5 py-1 focus-within:border-orange focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-orange">
        <MapPin size={15} strokeWidth={2} aria-hidden="true" className="shrink-0 text-muted" />
        <input
          id={id}
          role="combobox"
          aria-expanded={showList}
          aria-controls={`${id}-list`}
          aria-autocomplete="list"
          aria-activedescendant={showList && options[active] ? `${id}-opt-${active}` : undefined}
          aria-label="Find a locale on the map"
          value={term}
          placeholder="Find a locale…"
          onChange={(e) => {
            setTerm(e.target.value);
            setOpen(true);
            setActive(0);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 120)}
          onKeyDown={onKeyDown}
          className="min-w-[80px] flex-1 border-0 bg-transparent py-1 text-sm text-ink outline-none"
        />
      </div>
      {showList && (
        <ul
          id={`${id}-list`}
          role="listbox"
          className="absolute left-0 right-0 top-[calc(100%+4px)] z-30 m-0 max-h-[280px] list-none overflow-y-auto rounded-app-card border border-panel-border bg-white p-1 shadow-lg"
        >
          {options.length === 0 ? (
            <li className="px-3 py-2.5 text-[13px] text-muted">No locales on the map match.</li>
          ) : (
            options.map((r, i) => (
              <li
                key={r.localeId}
                id={`${id}-opt-${i}`}
                role="option"
                aria-selected={i === active}
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(r);
                }}
                onMouseEnter={() => setActive(i)}
                className={cx('cursor-pointer rounded-app px-3 py-2', i === active && 'bg-[rgba(143,68,32,.06)]')}
              >
                <div className="text-sm font-semibold text-ink">{r.name}</div>
                {(r.city || r.stateProvince) && (
                  <div className="text-xs text-muted">{[r.city, r.stateProvince].filter(Boolean).join(', ')}</div>
                )}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
